import React, { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon, Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';

import {
  AcademyTopicInput,
  academyTopicInputSelector,
  classFormSelector,
  contestSelector,
  contestTypeSelector,
  ContestTypeInput,
  CONTEST_TYPES,
  CREATE_CLASS_PAGE_TRACKINGS,
  LessonInput,
  LESSON_TYPES,
  setAcademyTopicInput,
  setClassType,
  setContest,
  setContestType,
  resetCreateClassForm,
  useCreateContestTemplateMutation,
  useLazyGetInterviewbitTestGroupQuery,
  validateContest,
} from 'features/classCreation';
import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import analytics from 'utils/analytics';
import Discussion from '../Discussion';
import Field from 'components/Field';
import Form from 'components/Form';
import GlobalLoader from 'components/GlobalLoader';
import GroupContestSummary from '../GroupsContestSummary';
import TaSkills from '../TaSkills';

export default function CreateContestForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const academyTopicInput = useSelector(academyTopicInputSelector);
  const contestLessonTest = useSelector(contestSelector);
  const classForm = useSelector(classFormSelector);
  const contestType = useSelector(contestTypeSelector);
  const [errors, setErrors] = useState(null);
  const state = useSelector((state) => state.createClassForm);
  const [
    createContestTemplate,
    { isLoading: isCreating, error, isSuccess, data: response },
  ] = useCreateContestTemplateMutation();
  const [
    getInterviewbitTests,
    { isLoading, data: interviewbitTests, error: interviewbitTestError },
  ] = useLazyGetInterviewbitTestGroupQuery();

  const handleAcademyTopicInputChange = useCallback(
    (newAcademyTopic) => {
      dispatch(setAcademyTopicInput(newAcademyTopic));
    },
    [dispatch]
  );

  const handleGetInterviewbitTests = useCallback(() => {
    getInterviewbitTests({
      testGroupId: contestLessonTest?.test_group_id,
      courseType: contestLessonTest?.course_type,
    });
  }, [contestLessonTest, getInterviewbitTests]);

  const handleContestLessonTestChange = useCallback(
    (newContest) => {
      dispatch(setContest(newContest));
    },
    [dispatch]
  );

  const handleContestTypeChange = useCallback(
    (type) => {
      setErrors(null);
      batch(() => {
        dispatch(setContestType(type));
        dispatch(setContest(null));
      });
    },
    [dispatch]
  );

  const handleCreateContest = useCallback(
    (event) => {
      event.preventDefault();
      const errors = validateContest(state);
      if (Object.keys(errors).length === 0) {
        setErrors(null);
        analytics.click({
          click_type: CREATE_CLASS_PAGE_TRACKINGS.createContest,
          custom: {
            contestType,
            contestTitle: academyTopicInput?.title,
          },
        });
        createContestTemplate(classForm);
      } else {
        setErrors(errors);
      }
    },
    [
      academyTopicInput?.title,
      classForm,
      contestType,
      createContestTemplate,
      state,
    ]
  );

  useEffect(() => {
    if (isSuccess) {
      navigate(`/classes/edit-classes/${response?.data?.id}`);
      batch(() => {
        dispatch(setClassType(null));
        dispatch(resetCreateClassForm());
      });
    }
  }, [dispatch, isSuccess, navigate, response]);

  useEffect(() => {
    if (error) {
      cogoToast.error(
        error.data?.message ||
          'Failed to create contest template, try again after sometime.'
      );
    }
  }, [error]);

  useEffect(() => {
    if (interviewbitTestError) {
      cogoToast.error(
        interviewbitTestError.data?.message || DEFAULT_ERROR_MESSAGE
      );
    }
  }, [interviewbitTestError]);

  return (
    <>
      <GlobalLoader isVisible={isLoading || isCreating} />
      <Form
        onSubmit={handleCreateContest}
        serverErrors={error?.data?.errors}
      >
        <Field
          label="Contest Activity"
          className="m-b-l"
          name="academy_topic"
          serverErrors={error?.data?.errors}
        >
          <AcademyTopicInput
            value={academyTopicInput}
            onChange={handleAcademyTopicInputChange}
          />
        </Field>
        <TaSkills errors={error?.data?.errors} />
        <Field
          label="Contest Type"
          className="dark bold"
        >
          <ContestTypeInput
            value={contestType}
            onChange={handleContestTypeChange}
          />
        </Field>
        <Field
          label="Contest Requirements"
          className="m-b-l"
          clientErrors={errors}
          name="contest"
          serverErrors={error?.data?.errors}
        >
          <div className="row align-c">
            <LessonInput
              lessonType={LESSON_TYPES.contest}
              contestType={contestType}
              value={contestLessonTest}
              onChange={handleContestLessonTestChange}
            />
            <Icon
              name="check-circle"
              className="h1 m-l-xs"
              onClick={handleGetInterviewbitTests}
            />
          </div>
          {contestType === CONTEST_TYPES.single && (
            <div className="h6 disabled m-t-xxs">
              If no contest id is added, then a blank test will be created for
              you, to which you can add problems later.
            </div>
          )}
        </Field>
        {contestType === CONTEST_TYPES.group && interviewbitTests?.data && (
          <GroupContestSummary interviewbitTests={interviewbitTests.data} />
        )}
        <Discussion errors={error?.data?.errors} />
        <Tappable
          className="btn btn-primary btn-long"
          disabled={isCreating}
          type="submit"
          component="button"
        >
          Create Contest
        </Tappable>
      </Form>
    </>
  );
}
